import {Tr} from "@/i18n/i18n";
import SceneUtils from "@/utils/scene-utils";
import store from "@/store";
import {Keys} from "@/constants/key";
import {SceneName} from "@/model/scene-name";
import {lazyInject} from "@/container";
import {TYPES} from "@/types";
import {SceneService} from "@/services/scene-service";
import TextUtils from "@/utils/text-utils";
import {Fonts} from "@/constants/font";
import {
    BuildingType,
    ClanAuthority,
    ClanBuildingInfo,
    ClanCandidate,
    ClanCandidateState,
    ClanControlAction,
    ClanInfo, ClanLogsDto,
    ClanmanInfo,
    ClanMinInfo,
    ClanShortInfo,
    ClanTaskInfo,
    ClanTaskOption,
    ClanTaskPerformer,
    DepositDto,
    FactoryGiftType, HallOptionInfo,
    RaidDifficulty
} from "@/store/clan/types";
import {FeedbackPanel} from "@/scenes.ui/feedback-panel";
import ListenerManager from "@/managers/listener-manager";
import {Settings} from "@/constants/settings";
import {NotificationManager} from "@/managers/notification-manager";
import OnceCaller from "@/utils/once-caller";
import {ClanActionsPanel} from "@/scenes.ui.clan/clan-actions-panel";
import {ConfirmationPanel} from "@/scenes.ui/confirmation-panel";
import {ListService} from "@/services/list-service";
import {ClanMemberPanel} from "@/scenes.ui.clan/clan-member-panel";
import {MemberActionPanel} from "@/scenes.ui.clan/member-action-panel";
import ClanUtils from "@/utils/clan-utils";
import {MenuService} from "@/services/menu-service";
import {ProfileService} from "@/services/profile-service";
import {PlaceService} from "@/services/place-service";
import {ClanRaidInfoPanel} from "@/scenes.ui.clan/clan-raid-info-panel";
import {ClanTaskPerformerPanel} from "@/scenes.ui.clan/clan-task-performer-panel";
import {ClanDepositPanel} from "@/scenes.ui.clan/clan-deposit-panel";
import {Listener} from "@/store/listener";
import {ClanService} from "@/services/clan-service";
import {PaymentNotificationService} from "@/services/payment-notification-service";
import {HealthService} from "@/services/health-service";
import {ExperienceService} from "@/services/experience-service";
import {MenuCreaturesNavPanel} from "@/scenes.ui/menu-creatures-nav-panel";
import {ShopService, TabName} from "@/services/shop-service";
import {ClanHeaderPanel} from "@/scenes.ui.clan/clan-header-panel";
import TimeUtils from "@/utils/time-utils";
import {SquareButton} from "@/scenes.ui/square-button";
import {ClanBuildingPanel} from "@/scenes.ui.clan/clan-building-panel";
import {ListTextPanel} from "@/scenes.ui/list-text-panel";
import {Amount} from "@/model/amount";
import {ClanCandidatePanel} from "@/scenes.ui.clan/clan-candidate-panel";
import {ClanInvitePanel} from "@/scenes.ui.clan/clan-invite-panel";
import {InfoPanel} from "@/scenes.ui/info-panel";
import CU from "../utils/c-u";
import {TextPanel} from "@/scenes.ui/text-panel";
import {ClanAboutControlPanel} from "@/scenes.ui.clan/clan-about-control-panel";
import {MenuNotification} from "@/scenes.ui/menu-notification";
import {RaidDifficultyPanel} from "@/scenes.ui.clan/raid-difficulty-panel";
import SoundUtils from "@/utils/sound-utils";
import ClanStore from "@/store/clan";
import PlayerStore from "@/store/player";
import {ListBlackouter} from "@/utils/list-blackouter";
import {HeroShopService} from "@/services/hero-shop-service";
import {ForumService} from "@/services/forum-service";
import {BottomPanel} from "@/scenes.ui/bottom-panel";
import {ClanThemePanel} from "@/scenes.ui.clan/clan-theme-panel";
import {PaymentChangeService} from "@/services/payment-change-service";
import {IapService} from "@/services/iap-service";
import {GameState} from "@/constants/game-state";
import SpriteUtils from "@/utils/sprite-utils";
import {InputTextPanel} from "@/scenes.ui/input-text-panel";
import {ChestContentType} from "@/store/chest/types";
import Scene = Phaser.Scene;
import Container = Phaser.GameObjects.Container;
import Text = Phaser.GameObjects.Text;
import Sprite = Phaser.GameObjects.Sprite;
import {ForceUtils} from "@/utils/force-utils";
import SkillUtils from "@/utils/skill-utils";
import CreaturePanel from "@/scenes.ui/creature-panel";
import {ClanHallPanel} from "@/scenes.ui.clan/clan-hall-panel";
import {HistoryDto, LogType} from "@/store/player/types";
import {LogPanel} from "@/scenes.ui/log-panel";
import {PagingPanel} from "@/scenes.ui.forum/paging-panel";
import {PlayerHistorySettings} from "@/scenes.ui/player-history-settings";
import {ClanLogPanel} from "@/scenes.ui/clan-log-panel";
import {Reloadable} from "@/scenes/reloadable";

export default class ClanScene extends Scene implements Listener, Reloadable {

    private canvas!: HTMLCanvasElement;

    @lazyInject(TYPES.SceneService)
    private sceneService!: SceneService;

    @lazyInject(TYPES.PlaceService)
    private placeService!: PlaceService;

    @lazyInject(TYPES.MenuService)
    private menuService!: MenuService;

    @lazyInject(TYPES.ListService)
    private listService!: ListService;

    @lazyInject(TYPES.ProfileService)
    private profileService!: ProfileService;

    @lazyInject(TYPES.ClanService)
    private clanService!: ClanService;

    @lazyInject(TYPES.HeroShopService)
    private heroShopService!: HeroShopService;

    @lazyInject(TYPES.PaymentNotificationService)
    private paymentNotificationService!: PaymentNotificationService;

    @lazyInject(TYPES.HealthService)
    private healthService!: HealthService;

    @lazyInject(TYPES.ShopService)
    private shopService!: ShopService;

    @lazyInject(TYPES.ExperienceService)
    private experienceService!: ExperienceService;

    @lazyInject(TYPES.ForumService)
    private forumService!: ForumService;

    @lazyInject(TYPES.PaymentChangeService)
    private paymentChangeService!: PaymentChangeService;

    @lazyInject(TYPES.IapService)
    private iapService!: IapService;

    private mainThemeSound!: Phaser.Sound.BaseSound | undefined;
    private selectSound!: Phaser.Sound.BaseSound | undefined;
    protected clickSound!: Phaser.Sound.BaseSound | undefined;
    protected completedSound!: Phaser.Sound.BaseSound | undefined;
    protected levelUpSound!: Phaser.Sound.BaseSound | undefined;
    protected awardSound!: Phaser.Sound.BaseSound | undefined;
    protected creatureClickSound!: Phaser.Sound.BaseSound | undefined;

    private clanHeaderPanel!: ClanHeaderPanel;
    private bottomPanel!: BottomPanel;
    private menuNavPanel!: MenuCreaturesNavPanel;

    private timeLabel!: Text;
    private timerId: number | undefined;

    private background!: Sprite;

    constructor() {
        super({ key: 'ClanScene' });
    }

    public preload(): void {
        SceneUtils.setCurrentScene(this);
        this.canvas = this.sys.game.canvas;
    }

    public create(): void {
        let self = this;

        this.mainThemeSound = SoundUtils.addSound(this, Keys.MUSIC_CLAN, {volume: Settings.MUSIC_VOLUME});
        this.selectSound = SoundUtils.addSound(this, Keys.SOUND_SELECT, {volume: Settings.MUSIC_VOLUME});
        this.selectSound = SoundUtils.addSound(this, Keys.SOUND_SELECT);
        this.clickSound = SoundUtils.addSound(this, Keys.SOUND_BUTTON_CLICK);
        this.completedSound = SoundUtils.addSound(this, Keys.SOUND_COMPLETED);
        this.levelUpSound = SoundUtils.addSound(this, Keys.SOUND_LEVEL_UP);
        this.awardSound = SoundUtils.addSound(this, Keys.SOUND_AWARD);
        this.creatureClickSound = SoundUtils.addSound(this, Keys.SOUND_CREATURE_CLICK, {volume: Settings.SHORT_TRACK_VOLUME});

        SoundUtils.play(this.mainThemeSound);

        ListenerManager.addClanInfoListener(this);

        this.sceneService!.fadeBlackout(this, this.canvas!.width, this.canvas!.height);

        let ci: ClanInfo | undefined = ClanStore.getClanInfo();

        if (!ci || !ci.clan) {
            new FeedbackPanel(this, Tr('vi.bolshe.ne.sostoite.v.klane'), undefined, false).show();
            self.switchScene(SceneName.ROAD_SCENE, {});
            return;
        }

        let clan = ci.clan;

        self.background = SpriteUtils.addSprite(this, 0, this.canvas.height / 2 / CU.S, GameState.CLAN_BACK_KEY)
            .setOrigin(0, 0.5);

        this.clanHeaderPanel = new ClanHeaderPanel(this, ci);
        this.add.existing(this.clanHeaderPanel);

        NotificationManager.subscribeClan();

        let xLeft = 45;
        let xRight = 555;
        let yStart = 125;
        let yDelta = 90;

        self.addMenuButton(xLeft, yStart, Keys.CL_CHAT, Tr('chat'), () => {
            CU.blockCanvas(self);
            ListenerManager.callChatOpenCallback(Settings.CLAN_CHAT_ROOM);
        });

        self.addMenuButton(xLeft, yStart + yDelta, Keys.CL_MEMEBERS, Tr('uchastniki'), () => {
            let blackouter: ListBlackouter = self.listService.showSmart(self, self.canvas, () => {});
            ClanStore.fetchClanMembers().then((cis: ClanmanInfo[]) => {
                blackouter.notifyLoaded(() => {
                    let members: ClanMemberPanel[] = [];
                    cis.forEach(ci => {

                        let cmp: ClanMemberPanel = new ClanMemberPanel(self, ci, (clanmanInfo: ClanmanInfo) => {
                            self.openMemberActionPanel(clanmanInfo, (updatedInfo: ClanmanInfo) => {
                                cmp.init(this, updatedInfo);
                            }, Tr('uchastniki'));
                        });

                        members.push(cmp);
                    });
                    blackouter.notifyInitialized(members, Tr('uchastniki'));
                });
            });
        });

        let raidTitle = Tr('zadanie');
        let taskButton = self.addMenuButton(xLeft, yStart + yDelta * 2, Keys.CL_RAID, raidTitle, () => {
            self.initTask(raidTitle);
        });
        let taskPlusSign = new MenuNotification(self, taskButton.x, taskButton.y);
        if (ClanUtils.promptClan(PlayerStore.getPlayer()!)) {
            taskPlusSign.show(0);
        }

        let aboutTitle = Tr('o.klane');
        self.addMenuButton(xLeft, yStart + yDelta * 3, Keys.CL_ABOUT, aboutTitle, () => {
            self.initAbout(ci!, aboutTitle);
        });

        if (ClanScene.canRecruit(ci)) {
            self.addMenuButton(xLeft, yStart + 4 * yDelta, Keys.CL_RECRUITING, Tr('rekruting'), () => {
                if (ci!.membersCurr >= ci!.membersMax) {
                    let ip: InfoPanel = new InfoPanel(this, this.canvas, Tr('ups'), Tr('clan.max.mem'),
                        Tr('v.vashem.klane.maksimalnoe.kolichestvo.uchastnikov'), () => {});
                    ip.openAnimated();
                } else {
                    self.initRecruiting();
                }
            });
        }

        if (ClanScene.canEditAdvertisement(ci)) {
            self.addMenuButton(xLeft, yStart + 5 * yDelta, Keys.CL_ADVERTISEMENT, Tr('adv'), () => {
                OnceCaller.doOnce("closeAdv", () => {
                    SoundUtils.play(self.selectSound);
                    let textPanel: InputTextPanel = new InputTextPanel(self, self.canvas, Tr('adv'),  Tr('adv.desc'),
                        Tr('adv.placeholder'), Tr('publish'),  clan.advertisement, (message: string) => {
                            textPanel.closePanel();
                            ClanStore.setClanAdvertisement(message).then(() => {
                                advertisement.setText(PlayerStore.getPlayer().name + ": " + message);
                                clan.advertisement = message;
                                showAdv = true;
                                advUpdaters.forEach((updater) => {updater();})
                            });
                        }, 180);
                    textPanel.openAnimated();
                });
            });
        }

        self.addMenuButton(xRight, yStart, Keys.CL_BUDGET, Tr('kazna'), () => {

            let clanDepositPanel = new ClanDepositPanel(this, this.canvas, clan.clanStat, PlayerStore.getPlayer()!, (depositDto: DepositDto) => {
                 OnceCaller.doOnce('depositValute', () => {
                    ClanStore.depositValute(depositDto).then(() => {
                        new FeedbackPanel(this, Tr('kazna.popolnena'), undefined, true).show();
                    });
                });
            });
            clanDepositPanel.open();

        });

        self.addMenuButton(xRight, yStart + yDelta, Keys.CL_BUILD, Tr('postroyki'), () => {
            self.initBuildings();
        });

        self.addMenuButton(xRight, yStart + yDelta * 2, Keys.CL_RATING, Tr('reyting').toUpperCase(), () => {
            self.clanService.openClanRating(this, this.canvas, (title, children) => {
                this.listService.show(this, this.canvas, title, children, () => {});
            }, (clanmanInfo: ClanmanInfo) => {
                self.openMemberActionPanel(clanmanInfo, (updatedInfo: ClanmanInfo) => {}, Tr('uchastniki'));
            }, (playerId: number, parentViewId: string) => {
                self.openProfile(playerId, parentViewId);
            });
        });

        self.addMenuButton(xRight, yStart + 5 * yDelta, Keys.CL_OTHER, Tr('prochee'), () => {
            let clanActionsPanel: ClanActionsPanel = new ClanActionsPanel(self, self.canvas, clan.level, ci!.me.clanPosition, ci!.authorities, () => {
                let cp = new ConfirmationPanel(self, self.canvas, () => {
                    ClanStore.resignFromClan().then(() => {
                        new FeedbackPanel(self, Tr('vi.ushli.v.otstavku'), undefined, true).show();
                        self.switchScene(SceneName.ROAD_SCENE, {});
                    });
                }, Tr('podtverzhdenie'), Tr('clan.resign'), Keys.UI_GOLDEN_CHECK, Tr('da'), Tr('net'), Keys.ATLAS_PROFILE);
                cp.openAnimated();
            }, () => {
                let cp = new ConfirmationPanel(self, self.canvas, () => {
                    ClanStore.leaveClan().then(() => {
                        new FeedbackPanel(self, Tr('vi.pokinuli.klan'), undefined, true).show();
                        self.switchScene(SceneName.ROAD_SCENE, {});
                    });
                }, Tr('podtverzhdenie'), Tr('clan.leave'), Keys.UI_GOLDEN_CHECK, Tr('da'), Tr('net'), Keys.ATLAS_PROFILE);
                cp.openAnimated();
            }, () => {
                ClanStore.fetchClanRaidOptions().then((cts: ClanTaskOption[]) => {
                    let rp = new RaidDifficultyPanel(self, self.canvas, cts, (rd: RaidDifficulty) => {
                        ClanStore.setClanRaidDifficulty(rd).then(() => {
                            new FeedbackPanel(self, Tr('slozhnost.skorrektirovana'), undefined, true).show();
                        });
                    });
                    rp.openAnimated();
                });
            }, () => {
                let ctp = new ClanThemePanel(this, this.canvas, clan, (themeNumber: number) => {
                    if (clan.clanThemeChanges >= Settings.CLAN_THEME_FREE_CHANGES && clan.clanStat.gems < Settings.CLAN_THEME_CHANGE_PRICE) {
                        new FeedbackPanel(self, Tr('u.klana.nedostatochno.sredstv'), undefined, false).show();
                    } else {
                        ClanStore.setClanTheme(themeNumber);
                        ClanUtils.loadClanTheme(self, self.canvas, themeNumber);
                        self.load.once(Phaser.Loader.Events.COMPLETE, () => {
                            SpriteUtils.setSprite(self, self.background, GameState.CLAN_BACK_KEY);
                        });
                        self.load.start();
                    }
                });
                ctp.open();
            }, ()=>{
                self.showClanHistory(self, self.canvas, <number>PlayerStore.getPlayer().clanId, self.listService);
            });
            clanActionsPanel.openAnimated();
        });

        self.addMenuButton(xRight, yStart + 3 * yDelta, Keys.CL_SHOP, Tr('h.shop'), () => {
            self.initHeroShop();
        });

        self.addMenuButton(xRight, yStart + 4 * yDelta, Keys.CL_FORUM, Tr('frm'), () => {
            self.initForum();
        });

        let showAdv = !!clan.advertisement && !ci.me.advertisementSeen;
        let  advUpdaters: { (): void; }[] = [];

        let advBg = SpriteUtils.addImage(this, CU.CANVAS_WIDTH/2 / CU.S, 150, Keys.ATLAS_UI, Keys.UI_STAT_WINDOW, 0.85, 1);
        advBg.setOrigin(0.5, 0);
        advBg.alpha = 0.7;
        advUpdaters.push(() => {advBg.setVisible(showAdv);})


        let advertisement = TextUtils.addTextAdvanced(this, clan.advertisementAuthor + ": " + clan.advertisement, CU.CANVAS_WIDTH/2 / CU.S, 150 + 37, '#fefefe', 12, 'normal',
            270, 'center', 2, Fonts.ARIAL_MT);
        advertisement.setOrigin(0.5, 0);
        advUpdaters.push(() => {advertisement.setVisible(showAdv);})

        let advertisementHeader = TextUtils.addTextAdvanced(this, Tr('adv'), CU.CANVAS_WIDTH/2 / CU.S, 150 + 23, '#e1af23', 14, 'normal',
            270, 'center', 2, Fonts.BENGALY_NORMAL);
        advUpdaters.push(() => {advertisementHeader.setVisible(showAdv);})

        let closeAdvButton = SpriteUtils.addImage(this, CU.CANVAS_WIDTH/2 / CU.S + 150, 155, Keys.ATLAS_PROFILE, "x", 0.7, 0.7);
        closeAdvButton.setInteractive().on('pointerdown', function() {
            OnceCaller.doOnce("closeAdv", () => {
                SoundUtils.play(self.selectSound);
                ClanStore.markClanAdvertisementSeen().then(() => {
                    showAdv = false;
                    advUpdaters.forEach((updater) => {updater();})
                });
            });
        });

        advUpdaters.push(() => {
            closeAdvButton.setVisible(showAdv);
        });
        advUpdaters.forEach((updater) => {updater();})

        ClanStore.fetchFactoryGift().then(giftDto => {
            if (giftDto && giftDto.giftType) {
                // console.log(giftDto)
                let y0 = -120;

                let factoryGiftButton = SpriteUtils.addImage(this, CU.CANVAS_WIDTH / 2 / CU.S, CU.CANVAS_HEIGHT/ CU.S  + y0 , Keys.CL_BLACK_SQUARE, undefined, 0.85, 1);
                factoryGiftButton.setOrigin(0.5, 0.85);
                factoryGiftButton.alpha = 0.7;
                factoryGiftButton.setInteractive().on('pointerdown', function () {
                    OnceCaller.doOnce("takeFactoryGift", () => {
                        SoundUtils.play(self.selectSound);
                        ClanStore.takeFactoryGift().then(clanGears => {
                            factoryGiftButton.visible = false;
                            factoryGiftCount.visible = false;
                            factoryGiftLabel.visible = false;
                            giftImage.visible = false;
                            redButton.visible = false;
                            new FeedbackPanel(self.scene.scene, Tr('nagrada.poluchena'), undefined, true).show();
                            self.clanHeaderPanel.setGears(clanGears.valueOf());
                        });
                    });
                });

                let getGiftImage = function (giftType: FactoryGiftType, xx:number, yy:number){
                    switch (giftType) {
                        case FactoryGiftType.GEMS:
                            return SpriteUtils.addSprite(self.scene.scene, xx, yy, Keys.ATLAS_BANK, Keys.UI_SHOP_GEMS_1, 0.6, 0.6);
                        case FactoryGiftType.RED_CRYSTALS:
                            return SpriteUtils.addSprite(self.scene.scene, xx, yy, Keys.IMG_RES_CRYSTAL);
                        case FactoryGiftType.BLUE_CRYSTALS:
                            return SpriteUtils.addSprite(self.scene.scene, xx, yy, Keys.IMG_RES_ELIXIR);
                        case FactoryGiftType.TICKETS:
                            return SpriteUtils.addSprite(self.scene.scene, xx, yy, Keys.IMG_TICKET_BIG, 0.85, 0.85);
                        case FactoryGiftType.GEARS:
                            return SpriteUtils.addSprite(self.scene.scene, xx, yy, Keys.CL_GEAR_RESOURCE, 0.6, 0.6);
                    }
                }
                let gearsReward = giftDto.giftType == FactoryGiftType.GEARS;

                let giftImage = getGiftImage(giftDto.giftType, CU.CANVAS_WIDTH / 2 / CU.S, CU.CANVAS_HEIGHT/ CU.S  + y0 - 85);
                let redButton = SpriteUtils.addSprite(this, CU.CANVAS_WIDTH / 2 / CU.S, CU.CANVAS_HEIGHT/ CU.S  + y0, Keys.CL_SMALL_RED_BUTTON, undefined, 0.85, 0.85);
                let factoryGiftCount = TextUtils.addTextAdvanced(this, TextUtils.formatMoney(giftDto.count), CU.CANVAS_WIDTH / 2 / CU.S, CU.CANVAS_HEIGHT/ CU.S  + y0 - 35, '#e1af23', 13, 'normal',
                    270, 'center', 2, Fonts.BENGALY_NORMAL);
                let factoryGiftLabel = TextUtils.addTextAdvanced(this, gearsReward? Tr('get.for.clan') : Tr('get')  , CU.CANVAS_WIDTH / 2 / CU.S, CU.CANVAS_HEIGHT/ CU.S  + y0, '#ffffff', gearsReward? 9: 10, 'normal',
                    270, 'center', 2, Fonts.ARIAL_MT);
                factoryGiftLabel.setStroke('#000000', 2);

                factoryGiftCount.alpha = 0;
                factoryGiftLabel.alpha = 0;
                factoryGiftButton.alpha = 0;
                giftImage.alpha = 0;
                redButton.alpha = 0;
                this.scene.scene.tweens.add({
                    targets: [factoryGiftCount, factoryGiftLabel, redButton, giftImage],
                    alpha: 1,
                    duration: 500
                });
                this.scene.scene.tweens.add({
                    targets: [factoryGiftButton],
                    alpha: 0.7,
                    duration: 500
                });
            }
        });

        const xButton = SpriteUtils.addImage(this!, xRight, 30, Keys.ATLAS_SHOP_UI, Keys.UI_SHOP_X).setOrigin(0.5, 0.5);
        xButton.setInteractive().on('pointerdown', function() {
            OnceCaller.doOnce("closeClanScene", () => {
                SoundUtils.play(self.selectSound);
                self.switchScene(SceneName.ROAD_SCENE, {});
            });
        });

        this.bottomPanel = new BottomPanel(this, 0, this.canvas.height, PlayerStore.getPlayer()!);
        this.add.existing(this.bottomPanel);
        ListenerManager.addPlayerListener(this);
        ListenerManager.setClanDetailsCallback((ci: ClanShortInfo) => this.openClanDetailsPanel(ci));

        this.initNavPanel();
        this.experienceService.setSceneAndCanvas(this, this.canvas, this.awardSound, this.creatureClickSound, (enabled: boolean) => {});
        this.healthService.initHealthTimer(this, this.canvas, () => this.openShop(),
            this.clickSound, this.levelUpSound, this.awardSound, (y: number) => this.experienceService.changeChildrenPosition(y));

        this.bottomPanel.setDepth(4000);
    }

    public createClanHallPanel(canStartBuilding: boolean){
        let self = this;
        ClanStore.fetchHallUpgradeOptions().then((options: HallOptionInfo[]) => {
            let clanHallPanel: ClanHallPanel = new ClanHallPanel(self, self.canvas, options, canStartBuilding, (gearsLeft:number) =>{
                self.clanHeaderPanel.setGears(gearsLeft);
            });
            clanHallPanel.openAnimated();
        });
    }

    private initNavPanel() {
        let myCreatures = PlayerStore.getCombativeCreatures();

        let calcX = this.canvas!.width - myCreatures.length * 30 * CU.S;
        let self = this;
        calcX += myCreatures.length == MenuCreaturesNavPanel.MAX_CREATURES_NO_ARROWS ? 17 : 0;
        this.menuNavPanel = new MenuCreaturesNavPanel(this, this.canvas!, calcX, this.canvas!.height - 80 * CU.S,
            myCreatures, (sceneName: SceneName, { creatureIndex: creatureIndex} ) => {
                OnceCaller.doOnce("toCreatureScene", () => {
                    self.switchScene!(SceneName.CREATURE_SCENE, { creatureIndex: creatureIndex});
                });
            }, MenuCreaturesNavPanel.MAX_CREATURES_NO_ARROWS);

        this.add.existing(this.menuNavPanel);
        this.menuNavPanel.setDepth(4000);
    }

    public openClanDetailsPanel(ci: ClanShortInfo){
        let self = this;
        this.clanService.showClanDetailsPanel(this, self.canvas, ci, (title, children) =>{
                this.listService.show(this, this.canvas, title, children, () => {
                    this.menuService.resourceToSide();
                    this.menuNavPanel!.setDepth(1000);
                });
                this.menuService.resourceToBottom();
                this.menuNavPanel!.setDepth(4000);
            },
            (clanmanInfo: ClanmanInfo) => {
                self.openMemberActionPanel(clanmanInfo, (updatedInfo: ClanmanInfo) => {}, Tr('uchastniki'));
            }, (playerId: number, parentViewId: string) => {
                self.openProfile(playerId, Tr("uchastniki"));
            });
    }

    private initAbout(ci: ClanInfo, aboutTitle: string) {
        let self = this;
        let blackouter: ListBlackouter = self.listService.showSmart(self, self.canvas, () => {});
        ClanStore.fetchClanAbout().then((content: string) => {
            blackouter.notifyLoaded(() => {
                let conts: Container[] = [];
                let tp: TextPanel = new TextPanel(self, content);
                conts.push(tp);
                if (ClanScene.canEditAbout(ci)) {
                    conts.push(new ClanAboutControlPanel(self, () => {
                        let cp: ConfirmationPanel = new ConfirmationPanel(self, self.canvas, () => {
                            ClanStore.restoreDefaultClanAbout().then((newContent) => {
                                tp.updateText(ClanStore.getClanAbout()!);
                                tp.parentContainer.removeAll(false);
                                self.listService.close(aboutTitle);
                                self.listService.init(this, this.canvas, aboutTitle, conts, () => {}, 67);
                            });
                        }, Tr('podtverzhdenie'), Tr('clan.txt.revert'), Keys.UI_GOLDEN_CHECK, Tr('da'), Tr('net'), Keys.ATLAS_PROFILE);
                        cp.openAnimated();
                    }, () => {
                        CU.blockCanvas(self);
                        ListenerManager.callClanAboutPageOpenCallback();
                        ListenerManager.setClanAboutPageCloseCallback(() => {
                            tp.updateText(ClanStore.getClanAbout()!);
                            tp.parentContainer.removeAll(false);
                            self.listService.close(aboutTitle);
                            self.listService.init(this, this.canvas, aboutTitle, conts, () => {}, 67);
                        });
                    }));
                }
                blackouter.notifyInitialized(conts, aboutTitle);
            });
        });
    }

    private initTask(raidTitle: string) {
        let self = this;
        let blackouter: ListBlackouter = this.listService.showSmart(this, this.canvas, () => {});

        ClanStore.fetchClanTaskInfo().then((cti: ClanTaskInfo) => {
            blackouter.notifyLoaded(() => {
                let conts: Container[] = [];
                let clanRaidInfoPanel: ClanRaidInfoPanel = new ClanRaidInfoPanel(this, cti,
                    (cont: ClanRaidInfoPanel, startY: number) => {

                        let sep = SpriteUtils.addSprite(this, 300, startY + 35, Keys.ATLAS_SHOP_UI, Keys.UI_SHOP_SEPARATOR, 0.9, 0.6);
                        cont.add(sep);

                        if (cti.countCurr >= cti.countRequired) {
                            let completedText = TextUtils.addTextAdvanced(self, Tr('zadanie.vipolneno'), 300, startY + 68,
                                '#4fff27', 14, 'normal', 500, 'center', 2, Fonts.MYRIADPRO_BOLD);
                            cont.add(completedText);
                            self.initTimerPanel(cont, cti, sep.y / CU.S + 15, Tr('novoe.zadanie.nachnetsja.zavtra.v') + cti.raidStartTime, raidTitle);
                        } else {
                            self.initTimerPanel(cont, cti, sep.y / CU.S + 2, Tr('start.zadanija') + cti.raidStartTime, raidTitle);
                        }
                    },
                    (cont: ClanRaidInfoPanel, startY: number) => {
                        self.initTimerPanel(cont, cti, startY, Tr('do.povtornogo.otpravlenija'), raidTitle);

                        let speedupButton = new SquareButton(self, 200, startY + 160, 0, Keys.UI_ART_GREEN_BUTTON,
                            undefined, Keys.IC_100_MITHRIL, Tr('uskorit'), false);

                        speedupButton.setCallback(() => {
                            if (cti.speedupPrice <= PlayerStore.getPlayer()!.mithril) {
                                SoundUtils.play(self.clickSound);
                                ClanStore.speedupClanRaid().then(() => {
                                    self.listService.close(raidTitle);
                                    self.initTask(raidTitle);
                                    new FeedbackPanel(self, Tr('uspeshno.uskoreno'), undefined, true).show();
                                });
                            } else {
                                SoundUtils.play(self.levelUpSound);
                                self.openShop();
                            }
                        });
                        cont.add(this.timeLabel);
                        cont.add(speedupButton);

                        let speedupPriceText = TextUtils.addText(self,
                            String(cti.speedupPrice), 200, startY + 194, '#ffffff', 25, 'bold', 200);
                        cont.add(speedupPriceText);

                        let roadButton = new SquareButton(self, 400, startY + 160, 0, Keys.UI_ART_BROWN_BUTTON,
                            Keys.ATLAS_PROFILE, Keys.UI_GOLDEN_CHECK, Tr('k.klanu'), false);
                        roadButton.setCallback(() => {
                            SoundUtils.play(self.selectSound);
                            self.listService.close(raidTitle);
                        })
                        cont.add(roadButton);

                    }, () => {
                        OnceCaller.doOnce("startClanRaid", () => {
                            SoundUtils.play(self.clickSound);
                            let faded = false;
                            let responseRecieved = false;

                            this.sceneService.fadeInBlackout(this, this.canvas!.width, this.canvas!.height, () => {
                                faded = true;
                                if (responseRecieved) {
                                    self.listService.close(raidTitle);
                                    self.doSwitchScene(SceneName.ROAD_SCENE, {});
                                }
                            }, 50);

                            self.placeService.startClanRaid(() => {
                                responseRecieved = true;
                                if (faded) {
                                    self.listService.close(raidTitle);
                                    self.doSwitchScene(SceneName.ROAD_SCENE, {});
                                }
                            });
                        });
                    }, () => {
                        OnceCaller.doOnce("endClanRaid", () => {
                            SoundUtils.play(self.clickSound);
                            let faded = false;
                            let responseRecieved = false;

                            this.sceneService.fadeInBlackout(this, this.canvas!.width, this.canvas!.height, () => {
                                faded = true;
                                if (responseRecieved) {
                                    self.listService.close(raidTitle);
                                    self.doSwitchScene(SceneName.ROAD_SCENE, {});
                                }
                            }, 50);

                            self.placeService.endClanRaid(false, () => {
                                responseRecieved = true;
                                if (faded) {
                                    self.listService.close(raidTitle);
                                    self.doSwitchScene(SceneName.ROAD_SCENE, {});
                                }
                            });
                        });
                    });

                conts.push(clanRaidInfoPanel);
                let performers: ClanTaskPerformer[] = cti.taskPerformers;
                performers.forEach(p => {
                    let ctpp = new ClanTaskPerformerPanel(this, cti.itemName, p, () => {
                        self.openMemberActionPanel(p.clanmanInfo, (updatedInfo: ClanmanInfo) => {
                            p.clanmanInfo = updatedInfo;
                            ctpp.init(this, cti.itemName, p);
                        }, raidTitle);
                    });
                    conts.push(ctpp);
                });
                blackouter.notifyInitialized(conts, raidTitle);
            });
        });
    }

    private initBuildings() {
        let self = this;
        let title = Tr('postroyki.klana');

        let blackouter: ListBlackouter = self.listService.showCustomSmart(self, self.canvas, () => {
            self.clanHeaderPanel.setupDefaultDepth();
        }, 195);

        ClanStore.fetchClanBuildings().then((cbs: ClanBuildingInfo[]) => {
            blackouter.notifyLoaded(() => {
                let members: Container[] = [];
                cbs.forEach(cb => {
                    let cbp: ClanBuildingPanel = new ClanBuildingPanel(self, cb, () => {
                        self.actionOnBuilding(cbp, members, title, ClanStore.startClanBuilding, Tr('clan.b.started'));
                    }, () => {
                        self.actionOnBuilding(cbp, members, title, ClanStore.speedupClanBuilding, Tr('uspeshno.uskoreno'));
                    }, () => {
                        self.actionOnBuilding(cbp, members, title, ClanStore.completeClanBuilding, Tr('clan.b.completed'));
                    });
                    members.push(cbp);
                });
                members.push(new ListTextPanel(self, Tr('sredstva.za.postroyki.vzimajutsja.iz.bjudzheta.klana'), 50 * CU.S));
                blackouter.notifyInitialized(members, title);
                self.clanHeaderPanel.setDepth(ListService.DEPTH + 1);
            });
        });
    }

    private initForum() {
        let player = PlayerStore.getPlayer();
        let self = this;
        this.forumService.openForum(this, this.canvas, this.listService, this.menuService, undefined,
            player.clanId, (playerId: number) => {
                self.openProfile(playerId, Tr('frm'));
            });
    }

    private initHeroShop() {
        let self = this;
        this.heroShopService.openShop(this, this.canvas, () => {
          self.menuService.bringFooterToBottom();
        }, (creatureId: string) => {
            self.listService.showManual(this, this.canvas, creatureId, () => {});
        }, self.paymentNotificationService, self.iapService, self.paymentChangeService);
        this.menuService.bringFooterToTop();
    }

    private initRecruiting() {
        let self = this;
        let title = Tr('rekruting');
        let blackouter: ListBlackouter = self.listService.showSmart(self, self.canvas, () => {});
        ClanStore.fetchClanCandidates().then((cps: ClanCandidate[]) => {
            blackouter.notifyLoaded(() => {
                let members: Container[] = [];
                cps.forEach(cp => {
                    let ccp: ClanCandidatePanel = new ClanCandidatePanel(self, cp, (cc: ClanCandidate) => {
                        let cip: ClanInvitePanel = new ClanInvitePanel(self, self.canvas, cc, () => {
                            self.openProfile(cc.playerId, title);
                        }, () => {
                            ClanStore.inviteToClan(cc.playerId).then((cmi: ClanMinInfo) => {
                                self.clanHeaderPanel.setMembCount(cmi);
                            });
                            cp.state = ClanCandidateState.INVITED;
                            ccp.init(self, cp);
                            new FeedbackPanel(self, Tr('igrok.priglashen.v.klan'), undefined, true).show();
                        }, () => {
                            ClanStore.rejectApplication(cc.playerId).then((cmi: ClanMinInfo) => {
                                self.clanHeaderPanel.setMembCount(cmi);
                            });
                            cp.state = ClanCandidateState.REJECTED;
                            ccp.init(self, cp);
                            new FeedbackPanel(self, Tr('rjctd'), undefined, true).show();
                        });
                        cip.openAnimated();
                    });
                    members.push(ccp);
                });
                blackouter.notifyInitialized(members, title);
            });
        });
    }

    private actionOnBuilding(cbp: ClanBuildingPanel, members: Container[], title: string,
                             action: (buildingType: BuildingType) => Promise<ClanBuildingInfo>, feedbackText: string) {
        let self = this;
        if (self.isEnoughResources(cbp.getClanBuildingInfo().price)) {
            SoundUtils.play(self.clickSound);
            action(cbp.getClanBuildingInfo().buildingType).then((newCb: ClanBuildingInfo) => {
                new FeedbackPanel(self, feedbackText, undefined, true).show();
                cbp.init(self, newCb);
                cbp.parentContainer.removeAll(false);
                self.listService.close(title);
                self.listService.init(self, self.canvas, title, members, () => {
                    self.clanHeaderPanel.setupDefaultDepth();
                }, 195);
                self.clanHeaderPanel.setDepth(ListService.DEPTH + 1);
                self.clanHeaderPanel.setBonus(newCb.clanExpRatio, newCb.clanDefenceChance, newCb.distance);
            });
        } else {
            SoundUtils.play(self.selectSound);
            new FeedbackPanel(self, Tr('u.klana.nedostatochno.sredstv'), undefined, false).show();
        }
    }

    private addMenuButton(x: number, y: number, imageKey: string, name: string, callback: () => unknown): Sprite {
        let button = SpriteUtils.addSprite(this, x, y, imageKey);
        let buttonTitle = TextUtils.addTextAdvanced(this, name, button.x / CU.S, button.y / CU.S + 40,
            '#ffffff', 10, 'normal', 100, 'center', 1, Fonts.ARIAL_MT);
        buttonTitle.setStroke('#000000', 2);
        button.setInteractive().on('pointerup', () => {
            OnceCaller.doOnce('clan_' + imageKey, callback);
        }, button);
        if (buttonTitle.width > 90) {
            buttonTitle.x+=5;
        }
        return button;
    }

    public showClanHistory(scene: Scene, canvas: HTMLCanvasElement, clanId: number, listService: ListService) {
        let blackouter: ListBlackouter = listService.showSmart(scene, canvas, () => {});
        let self = this;

        ClanStore.getClanLogs(clanId, 1).then((d: ClanLogsDto) => {
            blackouter.notifyLoaded(() => {
                self.initClanHistory(scene, canvas, clanId, d, blackouter, false);
            });
        });
    }

    private initClanHistory(scene: Scene, canvas:HTMLCanvasElement, playerId: number, d: ClanLogsDto, blackouter: ListBlackouter, re: boolean) {
        let self = this;
        let conts: Container[] = [];

        for (let l of d.logs) {
            let lp: ClanLogPanel = new ClanLogPanel(scene, l);
            conts.push(lp);
        }

        let pagingPanel = new PagingPanel(scene, d.pageCurr, d.pageMax, (page: number) => {
            ClanStore.getClanLogs(playerId, page).then((d: ClanLogsDto) => {
                self.initClanHistory(scene, canvas, playerId, d, blackouter, true);
            });
        });
        conts.push(pagingPanel);

        if (re) {
            blackouter.notifyReinitialized(conts, Tr('clan.history'));
        } else {
            blackouter.notifyInitialized(conts, Tr('clan.history'));
        }
    }

    public update(): void {
        ListenerManager.callUpdateListeners();
    }

    private switchScene(sceneName: SceneName, params: any) {
        ListenerManager.removePlayerListener(this);
        this.experienceService.stopListenPlayer();
        SoundUtils.stop(this.mainThemeSound);
        this.sceneService.startSceneWithParams(this, params, sceneName, this.canvas!.width, this.canvas!.height);
    }

    private doSwitchScene(sceneName: SceneName, params: any) {
        ListenerManager.removePlayerListener(this);
        this.experienceService.stopListenPlayer();
        SoundUtils.stop(this.mainThemeSound);
        this.sceneService.doStartSceneWithParams(this, params, sceneName, this.canvas!.width, this.canvas!.height);
    }

    private openProfile(playerId: number, parentViewId: string) {
        let self = this;
        self.listService.close(parentViewId);
        self.profileService.openProfile(this, this.canvas, () => {},
            () => {}, () => {}, () => {},
            (id: number, support: boolean) => {
                store.dispatch('setMailPartner', {id: id, support: support});
                this.menuService.openMailPage();
            },
            playerId,
            () => {}, () => {}, () => {}, () => {},
            () => {}, () => {}, () => {}, () => {}, () => {},
            self.listService, self.menuService);
    }

    private openMemberActionPanel(ci: ClanmanInfo, closeCallback: (updatedInfo: ClanmanInfo) => unknown, parentViewId: string) {
        let self = this;
        new MemberActionPanel(this, this.canvas, ci, () => {
            self.openProfile(ci.playerId, parentViewId);
        }, () => {
            store.dispatch('setMailPartner', {id: ci.playerId, support: false});
            self.menuService.openMailPage();
        }, (ca: ClanControlAction) => {
            ClanStore.participantAction(ci.playerId, ca).then((updatedInfo: ClanmanInfo) => {
                closeCallback(updatedInfo);
                new FeedbackPanel(this, ClanUtils.getActionFeedback(ca), undefined, true).show();
            });
        }).openAnimated();
    }

    onChange(): void {
        if (this.scene && this.scene.isActive()) {

            let clanInfo: ClanInfo | undefined = ClanStore.getClanInfo();

            if (clanInfo && clanInfo.clan && clanInfo.clan.clanStat && this.clanHeaderPanel) {
                this.clanHeaderPanel.setBudget(clanInfo.clan.clanStat);
            }

            this.bottomPanel.setBalance(PlayerStore.getPlayer()!);

            if (this.menuNavPanel && this.menuNavPanel.active) {
                this.menuNavPanel.destroy();
                this.initNavPanel();
            }
        }
    }

    private openShop() {
        let self = this;
        this.shopService.openShop(this, this.canvas, true, () => {
            self.menuService.destroyMenu();
        }, self.paymentNotificationService!, (creatureId: string) => {
            self.listService.showManual(this, this.canvas, creatureId, () => {});
        }, TabName.BANK, this.listService, this.paymentChangeService, this.iapService);
        this.menuService.setShowNavPanel(true);
        this.menuService.onChange();
    }

    private static remainSec(cti: ClanTaskInfo): number {
        return Math.floor((cti.raidOpensAt - TimeUtils.getServerTimeMs()) / 1000);
    }

    private initTimerPanel(cont: Container, cti: ClanTaskInfo, startY: number, text: string, raidTitle: string) {
        let self = this;
        if (self.timerId) {
            clearInterval(self.timerId);
            self.timerId = undefined;
        }

        let hText = TextUtils.addTextAdvanced(self, text, 300, startY + 45,
            '#ffffff', 14, 'normal', 500, 'center', 2, Fonts.MYRIADPRO_REGULAR);
        cont.add(hText);

        let remainSec = ClanScene.remainSec(cti);
        let durText = TimeUtils.getDurationText(remainSec);

        self.timeLabel = TextUtils.addTextAdvanced(self, durText, 300, startY + 70,
            '#ffde09', 22, 'normal', 500, 'center', 2, Fonts.MYRIADPRO_REGULAR);

        self.timerId = setInterval(() => {
            if (self.timeLabel && self.timeLabel.active && self.scene!.isActive()) {
                let remainSec = ClanScene.remainSec(cti);
                let durText = TimeUtils.getDurationText(remainSec);
                self.timeLabel.setText(durText);
                if (remainSec <= 0) {
                    clearInterval(self.timerId);
                    self.listService.close(raidTitle);
                    self.initTask(raidTitle);
                }
            }
        }, 1000);

        cont.add(this.timeLabel);
    }

    private isEnoughResources(a: Amount): boolean {
        let ci: ClanInfo | undefined = ClanStore.getClanInfo();
        let cs = ci!.clan.clanStat;
        return cs.gold >= a.gold && cs.gems >= a.mithril && cs.redCrystal >= a.crystal && cs.blueCrystal >= a.elixir;
    }

    private static canRecruit(ci: ClanInfo): boolean {
        return ci.authorities.includes(ClanAuthority.INVITE);
    }

    private static canEditAbout(ci: ClanInfo): boolean {
        return ci.authorities.includes(ClanAuthority.EDIT_ABOUT);
    }

    private static canEditAdvertisement(ci: ClanInfo): boolean {
        return ci.authorities.includes(ClanAuthority.EDIT_ADVERTISEMENT);
    }

    public reload(): void {
        this.sceneService.startSceneInstant(this, SceneName.CLAN_SCENE);
    }
}
